import React, { useEffect, useState } from 'react';
import MultiSelectBox from '@Common/MultiSelectBox/MultiSelectBox';
import MultiValueInput from '@Components/Common/MultiValueInput';
import Icon from '@Common/Icon';
import { RemoveWorkflowArea } from './styles';

interface WorkflowValidation {
	message: string;
	type: string;
	value: string;
	workflowEmails: Array<string>;
}

interface WorkflowSelectorProps {
	workflows: Array<{ eventId: string; name: string; actions: Array<{ actionId: string; name: string, validation: WorkflowValidation }> }>;
	selectedWorkflows: Array<{ eventId: string; actionId: string, workflowEmails: Array<string> }>; // Store multiple workflow configurations
	onChange: (workflows: Array<{ eventId: string; actionId: string }>) => void;
	isDisabled: boolean;
}

const WorkflowSelector: React.FC<WorkflowSelectorProps> = ({ workflows, selectedWorkflows, onChange, isDisabled }) => {
	const [workflowSelections, setWorkflowSelections] = useState(selectedWorkflows);

	// Ensure at least one workflow selection
	useEffect(() => {
		if (workflowSelections.length === 0) {
			setWorkflowSelections([{ eventId: '', actionId: '', workflowEmails: [] }]);
		}
	}, [workflowSelections]);

	const handleWorkflowChange = (index: number, eventId: string) => {
		const updatedSelections = [...workflowSelections];
		updatedSelections[index] = { ...updatedSelections[index], eventId, actionId: '' }; // Reset actionId on event change
		setWorkflowSelections(updatedSelections);
		onChange(updatedSelections);
	};

	const handleActionChange = (index: number, actionId: string) => {
		const updatedSelections = [...workflowSelections];
		updatedSelections[index] = { ...updatedSelections[index], actionId };
		setWorkflowSelections(updatedSelections);
		onChange(updatedSelections);
	};
	
	const handleActionEmailChange = (index: number, actionId: string, workflowEmails: Array<string>) => {
		const updatedSelections = [...workflowSelections];
		updatedSelections[index] = { ...updatedSelections[index], actionId, workflowEmails};
		setWorkflowSelections(updatedSelections);
		onChange(updatedSelections);
	};

	const addWorkflow = () => {
		setWorkflowSelections([...workflowSelections, { eventId: '', actionId: '', workflowEmails: [] }]);
	};

	const removeWorkflow = (index: number) => {
		const updatedSelections = workflowSelections.filter((_, i) => i !== index);
		setWorkflowSelections(updatedSelections);
		onChange(updatedSelections);
	};

	return (
		<div>
			{workflowSelections.map((selection, index) => {
				const actions = workflows.find((w) => w.eventId === selection.eventId)?.actions || [];
				const showWorkflowEmails = actions?.find((p) => p.actionId === selection.actionId)?.validation.type === 'email';

				return <RemoveWorkflowArea key={index} className='workflow-selection mb-3'>
					<div className='row mt-3'>
						<div className='col-1'>
							<Icon name='bars' />
						</div>
						<div className='col-10'>
							<MultiSelectBox
								name={`eventId-${index}`}
								label='Workflow'
								options={workflows}
								idField='eventId'
								valueField='name'
								selectedValues={selection.eventId}
								onChange={(eventId) => handleWorkflowChange(index, eventId)}
								isMulti={false}
								isRequired={false}
								isDisabled={isDisabled}
							/>
						</div>
						{ workflowSelections.length > 1 &&
							<div className='col-1'>
								<div className='task-step-remove text-center' onClick={() => removeWorkflow(index)}>
									<Icon name='trash-alt' />
								</div>
							</div>
						}
					</div>

					<div className='row'>
						<div className='col-1'></div>
						<div className='col-10'>
							{selection.eventId && (
								<MultiSelectBox
									name={`actionId-${index}`}
									label='Workflow Action'
									options={actions}
									idField='actionId'
									valueField='name'
									selectedValues={selection.actionId}
									onChange={(actionId) => handleActionChange(index, actionId)}
									isMulti={false}
									isRequired={false}
								/>
							)}
							<div className='col-1'></div>
						</div>
					</div>
					{ showWorkflowEmails && (
						<div className='row'>
							<div className='col-1'></div>
							<div className='col-10'>
								<MultiValueInput emails={selection.workflowEmails ?? []} setEmails={(emails) => handleActionEmailChange(index, selection.actionId, emails) } />
							</div>
							<div className='col-1'></div>
						</div>
					)}
				</RemoveWorkflowArea>
			})}
			{!isDisabled && (
				<button type='button' className='btn btn-link' onClick={addWorkflow}>
					Add Workflow
				</button>
			)}
		</div>
	);
};

export default WorkflowSelector;
